import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Loader2, Palmtree, Plane } from "lucide-react";
import { Reveal } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRAVEL_SITE = "https://edrinaafre.inteletravel.com";

const inputCls =
  "w-full border border-navy/25 bg-cream px-4 py-3 text-sm text-navy placeholder:text-navy/30 focus:border-navy focus:outline-none transition-colors duration-300";

const Field = ({ label, name, value, onChange, textarea = false, placeholder = "", required = false }) => (
  <label className="block">
    <span className="mb-2 block text-[10px] font-medium uppercase tracking-[0.25em] text-navy/50">
      {label}
      {required ? " *" : ""}
    </span>
    {textarea ? (
      <textarea
        data-testid={`travel-field-${name}`}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={inputCls}
      />
    ) : (
      <input
        data-testid={`travel-field-${name}`}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        required={required}
        className={inputCls}
      />
    )}
  </label>
);

const ChoicePills = ({ label, options, value, onPick, multi = false, name }) => (
  <div>
    <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.25em] text-navy/50">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = multi ? value.includes(opt) : value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`travel-pill-${name}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onPick(opt)}
            className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-[11px] font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
              active ? "border-navy bg-navy text-cream" : "border-navy/30 text-navy/70 hover:border-navy hover:text-navy"
            }`}
          >
            {active && <Check className="h-3 w-3" />}
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

const Section = ({ id, title, hint, open, onToggle, children }) => (
  <div className="border border-navy/20">
    <button
      type="button"
      data-testid={`travel-section-${id}`}
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-300 hover:bg-blush/40 md:px-8"
    >
      <div>
        <h3 className="font-playfair text-2xl tracking-tight text-navy">{title}</h3>
        <p className="mt-1 text-xs text-navy/50">{hint}</p>
      </div>
      <span
        className={`rounded-full border px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] transition-all duration-300 ${
          open ? "border-navy bg-navy text-cream" : "border-navy/40 text-navy/70"
        }`}
      >
        {open ? "Included" : "Add"}
      </span>
    </button>
    {open && <div className="grid gap-6 border-t border-navy/15 px-6 py-8 md:grid-cols-2 md:px-8">{children}</div>}
  </div>
);

const initialForm = {
  client_name: "", email: "", phone: "", address: "", vacation_budget: "", adults: "", travel_dates: "",
  destinations: "", insurance: "", children_ages: "", flexible: "",
  departure_city: "", airline_preference: "", seat_preferences: [],
  cruise_preferences: "", cruise_itinerary: "", cruise_length: "", pre_post_cruise: "", cabin_class: "",
  beverage_plan: "", beverage_plan_type: "",
  hotel_nights: "", hotel_rooms: "", room_types: [], hotel_preferences: "", hotel_features: [],
  car_preferences: "", car_categories: [], car_addons: "",
  tour_countries: "", tour_type: "", activity_level: "",
  enjoyed_hotels: "", enjoyed_cruises_resorts: "", activities: [], notes: "",
};

export default function TravelPage() {
  const [form, setForm] = useState(initialForm);
  const [open, setOpen] = useState({ air: false, cruise: false, hotel: false, car: false, tour: false });
  const [status, setStatus] = useState("idle");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleList = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const toggleSection = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/travel-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("sent");
      window.scrollTo({ top: document.getElementById("travel-form").offsetTop - 120, behavior: "smooth" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="travel-page">
      <div className="border-b border-navy/10 bg-sage/60">
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-20">
          <Reveal>
            <Link
              to="/"
              data-testid="travel-back-link"
              className="group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] text-navy/60 transition-colors duration-300 hover:text-navy"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Home
            </Link>
            <p className="mt-10 -rotate-2 font-caveat text-4xl font-semibold text-mustard">dream freely travel</p>
            <h1 className="mt-4 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight text-navy md:text-7xl lg:text-8xl">
              Your next chapter, booked with care.
            </h1>
            <p className="mt-8 max-w-xl text-sm leading-relaxed text-navy/70 md:text-base">
              Personalized travel planning with Dream Freely Travel — an independent InteleTravel agency. Cruises,
              resorts, air, tours and car rentals, planned around you. Fill out the travel form below and we'll craft
              your trip, or browse and book on the agent site.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={TRAVEL_SITE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="travel-agent-site-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-navy px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
              >
                <Plane className="h-4 w-4" />
                Visit the booking site
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href="#travel-form"
                data-testid="travel-form-anchor-btn"
                className="inline-flex items-center gap-3 rounded-full border border-navy px-8 py-4 text-xs font-medium uppercase tracking-[0.25em] text-navy transition-transform duration-300 hover:scale-95"
              >
                Fill out the travel form
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <div id="travel-form" className="mx-auto max-w-[1100px] px-6 py-20 md:px-12 md:py-24">
        <Reveal>
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-navy/50">Customer information form</p>
          <h2 className="mt-4 font-playfair text-4xl tracking-tight text-navy md:text-5xl">
            Tell us about your trip.
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy/60">
            Only your name and email are required — share as much or as little as you like. Add the trip types you're
            dreaming about and we'll take it from there.
          </p>
        </Reveal>

        {status === "sent" ? (
          <Reveal className="mt-12">
            <div data-testid="travel-success" className="border border-navy/25 bg-blush/60 px-8 py-14 text-center md:px-12">
              <Palmtree className="mx-auto h-8 w-8 text-mustard" />
              <p className="mt-6 -rotate-1 font-caveat text-4xl font-semibold text-navy">pack your dreams ♡</p>
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-navy/70">
                Your travel form is in. Edrina will review it personally and reach out to start planning — keep an
                eye on your inbox.
              </p>
              <button
                onClick={() => { setForm(initialForm); setStatus("idle"); }}
                data-testid="travel-another-btn"
                className="mt-8 rounded-full border border-navy px-7 py-3 text-xs font-medium uppercase tracking-[0.25em] text-navy transition-transform duration-300 hover:scale-95"
              >
                Plan another trip
              </button>
            </div>
          </Reveal>
        ) : (
          <form onSubmit={submit} className="mt-12 flex flex-col gap-6" data-testid="travel-form">
            <div className="grid gap-6 border border-navy/20 px-6 py-8 md:grid-cols-2 md:px-8">
              <Field label="Client Name" name="client_name" value={form.client_name} onChange={set} required />
              <Field label="Email" name="email" value={form.email} onChange={set} required />
              <Field label="Phone" name="phone" value={form.phone} onChange={set} />
              <Field label="Address" name="address" value={form.address} onChange={set} />
              <Field label="Vacation Budget" name="vacation_budget" value={form.vacation_budget} onChange={set} placeholder="e.g. $3,000 – $5,000" />
              <Field label="Number of Adults" name="adults" value={form.adults} onChange={set} />
              <Field label="Dates of Travel" name="travel_dates" value={form.travel_dates} onChange={set} placeholder="e.g. March 10–18, 2027" />
              <Field label="Number of Children and Ages" name="children_ages" value={form.children_ages} onChange={set} />
              <div className="md:col-span-2">
                <Field label="Destinations of Interest" name="destinations" value={form.destinations} onChange={set} placeholder="e.g. Amalfi Coast, Hawaii, a Caribbean cruise…" />
              </div>
              <ChoicePills label="Travel Insurance?" name="insurance" options={["Yes", "No"]} value={form.insurance} onPick={(v) => set("insurance", v)} />
              <ChoicePills label="Flexible on Dates?" name="flexible" options={["Yes", "No"]} value={form.flexible} onPick={(v) => set("flexible", v)} />
            </div>

            <Section id="air" title="Air Travel" hint="Departure city, airline and seat preferences" open={open.air} onToggle={() => toggleSection("air")}>
              <Field label="Departure City" name="departure_city" value={form.departure_city} onChange={set} />
              <Field label="Airline Preference (Frequent Flyer Programs)" name="airline_preference" value={form.airline_preference} onChange={set} />
              <div className="md:col-span-2">
                <ChoicePills label="Seat Preference" name="seat" multi options={["Economy", "Extra Leg Room/Premium", "Business Class", "First Class", "Window", "Bulkhead", "Forward", "Aisle", "Middle", "Wing"]} value={form.seat_preferences} onPick={(v) => toggleList("seat_preferences", v)} />
              </div>
            </Section>

            <Section id="cruise" title="Cruise Vacation" hint="Itinerary, cabin and beverage plans" open={open.cruise} onToggle={() => toggleSection("cruise")}>
              <Field label="Cruise Preferences (Frequent Cruiser Programs)" name="cruise_preferences" value={form.cruise_preferences} onChange={set} />
              <Field label="Cruise Itinerary" name="cruise_itinerary" value={form.cruise_itinerary} onChange={set} />
              <Field label="Cruise Length" name="cruise_length" value={form.cruise_length} onChange={set} placeholder="e.g. 7 nights" />
              <Field label="Cabin Class" name="cabin_class" value={form.cabin_class} onChange={set} />
              <ChoicePills label="Pre and Post Cruise Nights?" name="prepost" options={["Yes", "No"]} value={form.pre_post_cruise} onPick={(v) => set("pre_post_cruise", v)} />
              <ChoicePills label="Beverage Plan?" name="beverage" options={["Yes", "No"]} value={form.beverage_plan} onPick={(v) => set("beverage_plan", v)} />
              <Field label="Beverage Plan Type" name="beverage_plan_type" value={form.beverage_plan_type} onChange={set} />
            </Section>

            <Section id="hotel" title="Hotel & Resort Vacation" hint="Rooms, views and resort features" open={open.hotel} onToggle={() => toggleSection("hotel")}>
              <Field label="# of Nights" name="hotel_nights" value={form.hotel_nights} onChange={set} />
              <Field label="# of Rooms / Arrangement" name="hotel_rooms" value={form.hotel_rooms} onChange={set} />
              <div className="md:col-span-2">
                <ChoicePills label="Room" name="room" multi options={["Standard Room", "Garden View", "Ocean View/Front", "Other"]} value={form.room_types} onPick={(v) => toggleList("room_types", v)} />
              </div>
              <div className="md:col-span-2">
                <Field label="Hotel Preferences (Frequent Guest Programs)" name="hotel_preferences" value={form.hotel_preferences} onChange={set} />
              </div>
              <div className="md:col-span-2">
                <ChoicePills label="Features" name="features" multi options={["All Inclusive", "Adults Only", "Family Friendly", "Concierge Level", "Suite/Jr Suite", "On the Beach", "Near City Center", "Kids Club", "Near Air/Cruise Port", "Luxury Resort", "Activities On-Site", "Standard View", "Ocean View"]} value={form.hotel_features} onPick={(v) => toggleList("hotel_features", v)} />
              </div>
            </Section>

            <Section id="car" title="Car Rental" hint="Category and add-ons" open={open.car} onToggle={() => toggleSection("car")}>
              <Field label="Car Preferences (Frequent Renter Programs)" name="car_preferences" value={form.car_preferences} onChange={set} />
              <Field label="Add-Ons" name="car_addons" value={form.car_addons} onChange={set} placeholder="e.g. GPS, car seat, insurance" />
              <div className="md:col-span-2">
                <ChoicePills label="Car Category" name="carcat" multi options={["Compact", "Mid Size", "Full Size", "Luxury", "Other"]} value={form.car_categories} onPick={(v) => toggleList("car_categories", v)} />
              </div>
            </Section>

            <Section id="tour" title="Package Tour" hint="Countries, style and pace" open={open.tour} onToggle={() => toggleSection("tour")}>
              <Field label="Country or Countries of Interest" name="tour_countries" value={form.tour_countries} onChange={set} />
              <Field label="Activity Level" name="activity_level" value={form.activity_level} onChange={set} placeholder="e.g. relaxed, moderate, very active" />
              <ChoicePills label="Tour Style" name="tourtype" options={["Escorted", "Independent"]} value={form.tour_type} onPick={(v) => set("tour_type", v)} />
            </Section>

            <div className="grid gap-6 border border-navy/20 px-6 py-8 md:px-8">
              <h3 className="font-playfair text-2xl tracking-tight text-navy">Other Information</h3>
              <Field label="What hotels have you stayed in and enjoyed?" name="enjoyed_hotels" value={form.enjoyed_hotels} onChange={set} textarea />
              <Field label="What cruiselines and resorts have you enjoyed before, if any?" name="enjoyed_cruises_resorts" value={form.enjoyed_cruises_resorts} onChange={set} textarea />
              <ChoicePills label="What activities do you enjoy when travelling?" name="activities" multi options={["Sightseeing/History", "Culture/Arts", "Beach/Sun", "Active/Sports", "Wine/Culinary", "Shopping", "Spa"]} value={form.activities} onPick={(v) => toggleList("activities", v)} />
              <Field label="Notes" name="notes" value={form.notes} onChange={set} textarea placeholder="Anything else we should know — celebrations, accessibility needs, bucket-list moments…" />
            </div>

            {status === "error" && (
              <p data-testid="travel-error" className="text-sm text-rose">
                Something didn't send — please try again, or book directly on the agent site.
              </p>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="travel-form-submit-button"
              className="group flex items-center justify-center gap-3 rounded-full bg-navy px-10 py-5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-[0.98] disabled:opacity-60"
            >
              {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plane className="h-4 w-4" />}
              {status === "sending" ? "Sending…" : "Send my travel form"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
